import { Container, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
	SectionHeadline,
	LoadingSpinner,
	ReactSwitch,
	ModalBoxSectionEditProfile,
	Button,
	FontAwesome,
	Collapsible,
	NoData,
	CommunityPartnerTopJobCard
} from 'Elements';
import styles from 'partials/homepage/section/staff-section.module.scss';
import { useModal } from 'src/hooks/useModal';
import { Colors, CommunityPartnerMe } from 'Types';
import { useEffect, useState } from 'react';
import { useSearchJobs } from 'src/hooks/useJob';

type Props = {
	isLoadingProfile: boolean;
	isFetchingProfile: boolean;
	communityPartnerMe: CommunityPartnerMe;
	agentId: string;
	onChangeValue: (
		objKey: string,
		key: string,
		value: string | boolean
	) => void;
	topJobs: {
		enabled: boolean;
		headline: string;
	};
};

export const CommunityPartnerTopJopSection = ({
	isLoadingProfile,
	onChangeValue,
	communityPartnerMe,
	agentId,
	topJobs
}: Props) => {
	const { t } = useTranslation();
	const [openModal, toggleModal] = useModal();
	const perRow = 15;
	const [next, setNext] = useState(perRow);
	const [loading, setLoading] = useState(false);
	const { data, isLoading } = useSearchJobs({
		pageNumber: 1,
		pageSize: next,
		keyword: '',
		jobTags: [],
		jobActivities: [],
		locations: [],
		kind: null,
		postalCode: '',
		lon: null,
		lat: null,
		distance: null,
		topJob: true,
		businessSectors: [],
		...(communityPartnerMe?.commissionGroupInfo?.isPremium && {
			communityPartnerId: agentId
		}),
		categories: []
	});

	const handleMore = () => {
		setLoading(true);
		setNext(next + perRow);
	};

	useEffect(() => {
		setLoading(false);
	}, [next]);

	return (
		<section className="position-relative mb-5">
			<Container>
				{isLoadingProfile && (
					<div className="d-flex align-items-center justify-content-center">
						<LoadingSpinner />
					</div>
				)}
				{!isLoadingProfile && !isLoading && (
					<Collapsible
						header={
							<div
								className={`d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3 ${styles.switcherBox}`}>
								<div className="d-flex flex-wrap align-items-center gap-3">
									<h5 className="fs-5 fw-700 d-flex align-items-center">
										{' '}
										{t('menu.top-job')}
									</h5>
									<ReactSwitch
										size="md"
										checked={topJobs?.enabled}
										onChange={(val: boolean) => {
											onChangeValue(
												'topJobs',
												'enabled',
												val
											);
										}}
									/>
									<span className="d-flex align-items-center mb-0 py-0 cursor-pointer">
										{t('forms.show-top-job-in-home')}
									</span>
								</div>
								<div>
									<Button
										size="sm"
										color={Colors['white-gray']}
										onClick={() => toggleModal()}>
										<FontAwesome
											icon="circle-info"
											size="1x"
											className="text-gray me-1"
										/>
										{t('button.more')}
									</Button>
								</div>
							</div>
						}>
						{!loading && (
							<>
								<SectionHeadline
									title={topJobs?.headline}
									titlePlaceholder={`[${t(
										'placeholder.top-job-title'
									)}]`}
									titleColor="text-gray-5"
									className="mx-auto text-center"
									onUpdateTitle={(val) =>
										onChangeValue(
											'topJobs',
											'headline',
											val
										)
									}
								/>
								{data && data?.results?.length > 0 && (
									<Row>
										{data?.results
											?.slice(0, next)
											?.map((job) => {
												return (
													<CommunityPartnerTopJobCard
														key={
															job?.document?.jobId
														}
														job={
															job?.document as any
														}
													/>
												);
											})}
									</Row>
								)}
								{data?.results && data?.totalCount > next && (
									<div className="d-flex align-items-center justify-content-center mt-3 gap-3">
										<Button
											size="lg"
											color={Colors['border-gray-4']}
											label={
												t('button.more-jobs') as string
											}
											className="rounded-pill"
											onClick={handleMore}
										/>
									</div>
								)}
								{data && data?.totalCount === 0 && (
									<NoData message={t('title.no-job')} />
								)}
							</>
						)}
					</Collapsible>
				)}
			</Container>
			{!isLoadingProfile && !topJobs?.enabled && (
				<div className="overlay overlay_white overlay-box" />
			)}
			<ModalBoxSectionEditProfile
				isOpen={openModal}
				onClose={toggleModal}
				title="info.top-job-title"
				description="info.top-job-description"
			/>
		</section>
	);
};
